import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useEmailTemplateVariables } from '@/hooks/useEmailTemplateVariables';
import { useTranslation } from '@/hooks/useTranslation';
import { HelpCircle, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

interface TemplateVariablesProps {
  onVariableInsert: (variableKey: string) => void;
  showByDefault?: boolean;
  buttonText?: string;
  helpText?: string;
  isVisible?: boolean;
  onToggle?: () => void;
}

// Component for just the toggle button
export const TemplateVariablesButton: React.FC<{
  onClick: () => void;
  buttonText?: string;
}> = ({ onClick, buttonText }) => {
  const { t } = useTranslation();

  return (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      onClick={onClick}
      className='text-muted-foreground hover:text-foreground'
    >
      <HelpCircle className='h-4 w-4 mr-1' />
      {buttonText || t('emailTemplates.form.templateVariables')}
    </Button>
  );
};

// Component for the variables list
export const TemplateVariablesList: React.FC<{
  onVariableInsert: (variableKey: string) => void;
  helpText?: string;
  isVisible: boolean;
}> = ({ onVariableInsert, helpText, isVisible }) => {
  const { t, tDynamic } = useTranslation();
  const { variables, isLoading, isError, error } = useEmailTemplateVariables();

  // Transform variables with localized labels
  const templateVariables = variables.map(variable => {
    const translation = tDynamic(variable.labelKey);
    const hasTranslation = translation !== variable.labelKey;

    return {
      key: variable.key,
      label: hasTranslation ? translation : variable.key,
    };
  });

  const handleVariableClick = (variableKey: string) => {
    onVariableInsert(variableKey);
  };

  if (!isVisible) return null;

  return (
    <Alert className='mt-2 w-full'>
      <HelpCircle className='h-4 w-4' />
      <AlertDescription>
        <div className='space-y-2'>
          <p className='font-medium'>
            {helpText || t('emailTemplates.form.availableVariables')}
          </p>
          {isLoading ? (
            <div className='flex items-center gap-2 text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>{t('loading')}</span>
            </div>
          ) : isError ? (
            <div className='text-red-600 text-sm space-y-1'>
              <div>{t('error')}</div>
              {error && (
                <div className='text-xs font-mono bg-red-50 p-2 rounded border border-red-200'>
                  {error.message || 'Unknown error occurred'}
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-wrap gap-2'>
              {templateVariables.length > 0 ? (
                templateVariables.map(({ key, label }) => (
                  <Badge
                    key={key}
                    variant='secondary'
                    className='cursor-pointer hover:bg-secondary/80 transition-colors'
                    onClick={() => handleVariableClick(key)}
                    title={`Insert {{${key}}}`}
                  >
                    {label}
                  </Badge>
                ))
              ) : (
                <div className='text-muted-foreground text-sm'>
                  {t(
                    'emailTemplates.form.noVariablesAvailable',
                    'No variables available'
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

// Original combined component for backward compatibility
export const TemplateVariables: React.FC<TemplateVariablesProps> = ({
  onVariableInsert,
  showByDefault = true,
  buttonText,
  helpText,
  isVisible,
  onToggle,
}) => {
  const [internalShowVariables, setInternalShowVariables] =
    useState(showByDefault);

  // Use external state if provided, otherwise use internal state
  const showVariables =
    isVisible !== undefined ? isVisible : internalShowVariables;
  const toggleVariables =
    onToggle || (() => setInternalShowVariables(!internalShowVariables));

  return (
    <>
      <TemplateVariablesButton
        onClick={toggleVariables}
        buttonText={buttonText}
      />
      <TemplateVariablesList
        onVariableInsert={onVariableInsert}
        helpText={helpText}
        isVisible={showVariables}
      />
    </>
  );
};

export default TemplateVariables;
